import DOMPurify from 'dompurify'

// Sanitize configs for the operator timeline, kept out of the component file so
// they can be unit-tested and shared without tripping react-refresh.

// Chat / note bodies: minimal inline formatting only.
export const sanitizeConfig = {
  ALLOWED_TAGS: [
    'b',
    'i',
    'em',
    'strong',
    'a',
    'br',
    'p',
    'ul',
    'ol',
    'li',
    'span',
    'div',
    'blockquote',
    'pre',
    'code',
  ],
  ALLOWED_ATTR: ['href', 'rel'],
}

// Email bodies carry richer markup (tables, headings, inline styles for
// signatures) plus inline images — logos, embedded screenshots — which Chatwoot
// rehosts on its own domain, so rendering them shows the mail as the sender
// intended instead of a text-only husk. DOMPurify still blocks
// <script>, event handlers and dangerous `src` schemes; the image hook below
// adds no-referrer + lazy-loading so an image host can't fingerprint the operator.
export const emailSanitizeConfig = {
  ALLOWED_TAGS: [
    'b',
    'i',
    'em',
    'strong',
    'u',
    'a',
    'br',
    'p',
    'span',
    'div',
    'blockquote',
    'pre',
    'code',
    'ul',
    'ol',
    'li',
    'hr',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'img',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'td',
    'th',
  ],
  ALLOWED_ATTR: [
    'href',
    'rel',
    'target',
    'style',
    'align',
    'colspan',
    'rowspan',
    'src',
    'alt',
    'title',
    'width',
    'height',
  ],
}

// A notification CTA must be a real clickable link that does NOT navigate the
// operator away from the inbox. The module-scope hook below only adds rel to
// anchors that already declare a target; here we also force target="_blank" so
// the CTA opens in a new tab. Registered as a scoped hook around the sanitize
// call (added then removed) so it never leaks into unrelated DOMPurify callers
// (private notes / chat bodies), which must keep their own link behaviour.
function forceBlankLinkTarget(node: Element): void {
  if (node.nodeName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noopener noreferrer')
  }
}

// Sanitize a system-notification HTML body for display. Reuses the email
// allow-list (structure + inline styling survive, <script>/handlers/beacons are
// still stripped) and additionally forces every CTA link to open safely in a new
// tab. Returns sanitized HTML ready for dangerouslySetInnerHTML.
export function sanitizeSystemNotificationHtml(body: string): string {
  DOMPurify.addHook('afterSanitizeAttributes', forceBlankLinkTarget)
  try {
    return DOMPurify.sanitize(body, emailSanitizeConfig)
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes')
  }
}

// Harden the markup we let through. Registered once at module scope; each branch
// is a no-op for the plain-text message sanitize (which allows no <img>, no
// `target` and no `style`) and for any other DOMPurify caller.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  // Images: `no-referrer` keeps the inbox URL out of the request; lazy loading
  // defers offscreen decodes.
  if (node.nodeName === 'IMG') {
    node.setAttribute('referrerpolicy', 'no-referrer')
    node.setAttribute('loading', 'lazy')
  }
  // A `target="_blank"` link must not hand the opened page window.opener access
  // (tab-nabbing) or leak the referrer — force noopener/noreferrer on any anchor
  // that opens a new context.
  if (node.nodeName === 'A' && node.getAttribute('target')) {
    node.setAttribute('rel', 'noopener noreferrer')
  }
  // DOMPurify doesn't parse CSS, so an inline style can smuggle a tracking beacon
  // via url(...) (e.g. `background-image: url(https://tracker/pixel.png)`). Strip
  // any url() from style values, dropping the attribute if nothing else remains.
  const style = node.getAttribute('style')
  if (style && /url\(/i.test(style)) {
    const cleaned = style.replace(/url\([^)]*\)/gi, '').trim()
    if (cleaned) {
      node.setAttribute('style', cleaned)
    } else {
      node.removeAttribute('style')
    }
  }
})
